import React, { PureComponent } from 'react';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { gettext, translationPropType } from '@eventbrite/i18n';

import { UploaderLayoutManager } from '@eventbrite/eds-uploader';
import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import PreviewElement from './imageUploaderPreview';
import ObjectFitImage from './ObjectFitImage';
import { isPromise } from '@eventbrite/eds-uploader';
import {
    mbToBytes,
    isFileSizeValid,
    isFileTypeValid,
    IMAGE_CONTENT_TYPES,
} from '@eventbrite/eds-utils';

import { Icon } from '@eventbrite/eds-icon';
import { ICON_TYPE_PROP_TYPE } from '@eventbrite/eds-icon';

import { loadImageFileDataURL } from './utils';
import {
    IMAGE_DROPZONE_DEFAULT_ICON_COLOR,
    IMAGE_DROPZONE_ACTIVE_ICON_COLOR,
    IMAGE_DROPZONE_ERROR_ICON_COLOR,
    UPLOAD_STATUS_FAILED,
    UPLOAD_STATUS_SUCCESS,
    UPLOAD_STATUS_UPLOADING,
} from './constants';

import './imageUploader.scss';

import { Photo } from '@eventbrite/eds-iconography';

const defaultAllowedContentTypes = [...IMAGE_CONTENT_TYPES];

/**
 * The element shown when the uploader is awaiting interaction
 */
const DropzoneElement = ({
    headline,
    description,
    iconType,
    iconAnimated,
    isActive,
    errorContent,
}) => {
    const dropzoneClassName = classNames(
        {
            'eds-image-uploader__dropzone--active': isActive,
            'eds-image-uploader__dropzone--error': errorContent,
        },
        'eds-image-uploader__dropzone',
    );

    let iconColor = IMAGE_DROPZONE_DEFAULT_ICON_COLOR;
    let content = description;

    if (errorContent) {
        content = '';
        iconColor = IMAGE_DROPZONE_ERROR_ICON_COLOR;
    }

    if (isActive) {
        iconColor = IMAGE_DROPZONE_ACTIVE_ICON_COLOR;
    }

    return (
        <div
            className={dropzoneClassName}
            data-spec="image-uploader-dropzone-element"
        >
            <Icon
                type={iconType}
                color={iconColor}
                isBlockLevel={true}
                animated={!!iconAnimated}
                size="medium"
            />
            <div
                className="eds-image-uploader__dropzone-headline eds-text-bl"
                data-spec="image-uploader-headline"
            >
                {headline}
            </div>
            <div
                className="eds-image-uploader__dropzone-description eds-text-bm"
                data-spec="image-uploader-description"
            >
                {content}
                {errorContent}
            </div>
        </div>
    );
};

/**
 * The element shown when the user is waiting for the file to upload
 */
const LoadingElement = ({
    url,
    progress,
    localLoadingElement,
    errorContent,
}) => {
    // For uploading local file to browser, show a simple default loading view
    // which looks exact like the dropzone but with an animated icon.
    if (progress === 0 && localLoadingElement) {
        return (
            <div data-spec="image-uploader-loading-element">
                {localLoadingElement}
            </div>
        );
    }

    // Note: We used to show a preview of the local image during upload. However, due to https://jira.evbhome.com/browse/EB-153553
    // we needed to remove the preview of the local image because the orientation could be incorrect, causing confusion for users.
    // We may go back and fix this at some point, but for now, we feel it is best to show no image rather than an
    // image with the incorrect orientation. We did this by making the ObjectFitImage visibility set to hidden.

    let content;

    if (url) {
        content = (
            <ObjectFitImage
                imageSrc={url}
                imageAlt={gettext('Uploaded image preview')}
                imageClassName="eds-image-uploader__loading-image"
            />
        );
    }

    return (
        <div
            className="eds-image-uploader__loading"
            data-spec="image-uploader-loading-element"
        >
            <div className="eds-image-uploader__loading-image-container">
                <div className="eds-image-uploader__loading-image">
                    {content}
                </div>
                <div className="eds-image-uploader__loading-progress">
                    <div className="eds-image-uploader__loading-progress-bar">
                        <ProgressIndicator
                            style="light"
                            progress={progress}
                            type="determinate"
                            shape="circular"
                        />
                    </div>
                    {errorContent}
                </div>
            </div>
        </div>
    );
};

/**
 * Component helper to show each error separately
 */
const ErrorOutput = ({ errors }) => {
    const errorElements = errors.map((error) => <div key={error}>{error}</div>);

    return (
        <div
            className="eds-image-uploader__errors"
            data-spec="image-uploader-errors"
        >
            {errorElements}
        </div>
    );
};

/**
 * An drag-and-drop image uploader that shows a live file preview
 * while the file is uploading.
 * @extends PureComponent
 * @example
 * // <ImageUploader
 * //     imageAlt={gettext('Your Event Image')}
 * //     imageUrl="url_or_data_uri"
 * //     onRemove={func_to_disassociate_image_id}
 * //     onReceiveFile={func_that_uploads_and_returns_promise_that_resolves_with_image_url}
 * // />
 */
export default class ImageUploader extends PureComponent {
    static propTypes = {
        /**
         * Function that's invoked when the user clicks "Remove"
         */
        onRemove: PropTypes.func.isRequired,

        /**
         * Function that's invoked when the user has chosen a file
         */
        onReceiveFile: PropTypes.func.isRequired,

        /**
         * Alt attribute assigned to the image preview after image is uploaded.
         * This should be helpful, descriptive text that accurate represents
         * the purpose or meaning of the image (such as "Event Listing Image")
         */
        imageAlt: translationPropType.isRequired,

        /**
         * Function that's invoked when the user clicks "Crop" button.
         * The "Crop" button is optional will only appear if this
         * function is provided.
         */
        onCrop: PropTypes.func,

        /**
         * URL of prepopulated image to be shown in the preview
         */
        imageUrl: PropTypes.string,

        /**
         * Primary text shown in the initial uploader state.
         * This should be a call-to-action to the user ("Choose an Image")
         */
        headline: PropTypes.node,

        /**
         * Secondary text shown in the initial uploader state.
         * This can be longer descriptive text to help the user choose an appropriate image.
         */
        description: PropTypes.node,

        /**
         * Icon shown above the headline in the initial uploader state.
         */
        iconType: ICON_TYPE_PROP_TYPE,

        /**
         * The maximum acceptable filesize specified in megabytes.
         * Files larger than this number will be rejected with an error message.
         */
        maxFileSize: PropTypes.number,

        /**
         * A list of acceptable content-types for dropped files.
         * Files of any type other than those in this list will be rejected with an error message.
         */
        allowedContentTypes: PropTypes.arrayOf(PropTypes.string),

        /**
         * The error message shown to the user if they drop an inappropriate filetype.
         * This can be specified via props to provide a more specific actionable message
         * such as "Please choose a JPG, PNG, or GIF"
         */
        contentTypeError: translationPropType,

        /**
         * Function that's invoked when the "progress image" has loaded.  "Progress image"
         * is the image shown to the user while the image is being uploaded.  Your callback
         * will be provided a dataURI of the progress image.  If your onProgressImageLoaded
         * returns a promise, the result of the promise will be shown to the user while image
         * is being uploaded.  This gives the implementer some control over the progress image.
         */
        onProgressImageLoaded: PropTypes.func,
        /**
         * Function that's invoked when the user has chosen a file and returns
         * the status of the Uploader process of uploading.
         * Returns 3 possibles strings: 'uploading', 'uploaded', 'failed'.
         */
        onUploadStatusChange: PropTypes.func,
    };

    static defaultProps = {
        headline: gettext('Add Image'),
        description: gettext('Drag-drop or click here to choose a file'),
        iconType: <Photo />,

        maxFileSize: 10,
        allowedContentTypes: defaultAllowedContentTypes,
        contentTypeError: gettext('The selected file is not a valid type'),
    };

    constructor(props, context) {
        super(props, context);
        this.state = {
            previewImageUrl: props.imageUrl,
            errors: undefined,
            progressImageUrl: undefined,
            progress: undefined,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { imageUrl } = nextProps;

        // the implementer should manage whether the uploader is populated.
        // this is important in the context of a form field whose populated state
        // could be for any number of reasons such as form reset, etc
        this.setState({
            previewImageUrl: imageUrl,
        });
    }

    _handleRemoveFile() {
        const { onRemove } = this.props;

        this.setState({
            previewImageUrl: undefined,
        });
        onRemove();
    }

    _validateFile(file) {
        const {
            maxFileSize,
            allowedContentTypes = [],
            contentTypeError,
        } = this.props;

        const errors = [];

        if (maxFileSize && !isFileSizeValid(file, mbToBytes(maxFileSize))) {
            errors.push(
                gettext(
                    'The maximum size allowed is %(maxFileSize)sMB.  Please resize your image and try again.',
                    { maxFileSize },
                ),
            );
        }

        if (
            allowedContentTypes.length &&
            !isFileTypeValid(file, allowedContentTypes)
        ) {
            errors.push(contentTypeError);
        }

        if (errors.length) {
            this.setState({ errors });
            return false;
        }

        return true;
    }

    _handleReceiveFile(file) {
        const { onReceiveFile, onUploadStatusChange } = this.props;

        if (!this._validateFile(file)) {
            return Promise.reject();
        }

        this.setState({
            progress: 0,
            errors: undefined,
        });

        if (onUploadStatusChange) {
            onUploadStatusChange(UPLOAD_STATUS_UPLOADING);
        }

        this._loadProgressImage(file);

        const promise = onReceiveFile(file, this._handleProgress.bind(this));

        if (!isPromise(promise)) {
            throw new Error(
                'ImageUploader.onReceiveFile prop should return a promise',
            );
        }

        return promise
            .then(this._handleUploadSuccess.bind(this))
            .catch(this._handleUploadFailure.bind(this));
    }

    _loadProgressImage(file) {
        loadImageFileDataURL(file)
            .then(this._handleProgressImageLoaded.bind(this))
            .catch(noop);
    }

    _handleProgressImageLoaded(progressImageUrl) {
        const { onProgressImageLoaded } = this.props;

        this.setState({ progressImageUrl });

        if (onProgressImageLoaded) {
            const result = onProgressImageLoaded(progressImageUrl);

            if (isPromise(result)) {
                /**
                 * This allows implementing parties to have control over the image shown
                 * while uploading.  This is intially useful for integrating ImageCropper.
                 * The user can crop the image prior to upload then dictate what's shown
                 * during upload (such as a cropped version of the image).
                 */
                result.then((modifiedProgressImageUrl) => {
                    this.setState({
                        progressImageUrl: modifiedProgressImageUrl,
                    });
                });
            }
        }
    }

    _handleUploadSuccess(previewImageUrl) {
        const { onUploadStatusChange } = this.props;

        this.setState({
            previewImageUrl,
            errors: undefined,
            progressImageUrl: undefined,
            progress: undefined,
        });

        if (onUploadStatusChange) {
            onUploadStatusChange(UPLOAD_STATUS_SUCCESS);
        }
    }

    _handleUploadFailure(errorMessage) {
        const { onUploadStatusChange } = this.props;

        let errors;

        /**
         * Errors should be explicitly passed by implementer.
         * The implementer has control of the actual file transport, therefore
         * they have all knowledge of what failure occurred (validation
         * failures are handled separately in this file).
         * This also allows cancelling for other reasons, such as user driven
         * which should not display an error message.
         */
        if (errorMessage) {
            errors = [errorMessage];
        }

        this.setState({
            previewImageUrl: undefined,
            errors,
            progressImageUrl: undefined,
            progress: undefined,
        });

        if (onUploadStatusChange) {
            onUploadStatusChange(UPLOAD_STATUS_FAILED);
        }
    }

    _handleProgress(progress) {
        this.setState({
            progress,
        });
    }

    render() {
        const {
            imageAlt,
            headline,
            description,
            iconType,
            onCrop,
        } = this.props;

        const {
            previewImageUrl,
            progressImageUrl,
            progress,
            errors,
        } = this.state;

        let errorContent;

        if (errors && errors.length) {
            errorContent = <ErrorOutput errors={errors} />;
        }

        const dropzone = (
            <DropzoneElement
                headline={headline}
                description={description}
                iconType={iconType}
                errorContent={errorContent}
            />
        );

        const dragDropzone = (
            <DropzoneElement
                headline={headline}
                description={description}
                iconType={iconType}
                isActive={true}
            />
        );

        const preview = (
            <PreviewElement
                url={previewImageUrl}
                altText={imageAlt}
                onRemove={this._handleRemoveFile.bind(this)}
                onCrop={onCrop}
            />
        );

        const localLoadingElement = (
            <DropzoneElement
                headline={headline}
                description={description}
                iconType={iconType}
                iconAnimated={true}
                errors={errors}
            />
        );

        const loading = (
            <LoadingElement
                url={progressImageUrl}
                progress={progress}
                localLoadingElement={localLoadingElement}
                errorContent={errorContent}
            />
        );

        return (
            <div className="eds-image-uploader">
                <UploaderLayoutManager
                    dropzoneElement={dropzone}
                    dragDropzoneElement={dragDropzone}
                    windowDragDropzoneElement={dragDropzone}
                    loadingElement={loading}
                    previewElement={preview}
                    showPreview={!!previewImageUrl}
                    onReceiveFile={this._handleReceiveFile.bind(this)}
                />
            </div>
        );
    }
}
